/**
 * Preferences › Voice (settings pocket spec §6), grouped by who is talking.
 * Speaking: read replies aloud, the voice (with a preview in the desktop app,
 * where the voices live, and a voice language's download while it runs),
 * staying quiet over music, videos and calls (desktop) and,
 * on Windows, the voice engine.
 * Listening (desktop): the microphone, the language, the dictation engine -
 * the platform recogniser or one of the local models - and the wake word.
 * Then Dictation and its recent dictations. Each engine row says in one line
 * what the chosen engine trades.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  Button,
  PocketRow,
  PocketSection,
  SelectField,
  ToggleSwitch,
} from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { isTauri } from "@/lib/is-tauri";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import {
  DENKER_AGENT_VOICES,
  ensureSystemVoicesResolved,
  isDenkerAgentVoiceAudioReady,
  isVoiceEngineSelectable,
  previewDenkerAgentVoice,
  previewWindowsSystemVoice,
  primeDenkerAgentVoiceAudio,
  refreshWindowsSystemVoices,
  subscribeSystemVoices,
  systemVoiceMappingRevision,
  warmDenkerAgentVoiceAudio,
  windowsSystemVoices,
  type DenkerAgentVoiceId,
} from "@/desktop/denker-agent-voice";
import {
  requestWhisperMicrophone,
  type DictationEngineChoice,
} from "@/desktop/dictation-engine-options";
import { track } from "@/desktop/observability";
import { usePocketVoiceDownload } from "@/desktop/use-pocket-voice-download";
import { useVoiceEngineChoice } from "@/desktop/use-voice-engine-choice";
import {
  openWindowsSpeechSettingsSafely,
  VOICE_ENGINE_OPTIONS,
  type VoiceEngineChoice,
} from "@/desktop/voice-engine-options";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { SettingsFieldError } from "./settings-text-field";
import { VoiceDictationEngineRow } from "./voice-dictation-engine-setting";
import { VoiceDictationSection } from "./voice-dictation-section";
import { VoiceLanguageRow } from "./voice-language-setting";
import { VoiceMicrophoneRow } from "./voice-microphone-setting";
import { pocketVoiceLine } from "./voice-pocket-line";
import { VoiceRecentDictations } from "./voice-recent-dictations";
import { VoiceWakeRow } from "./voice-wake-setting";

/** Voice choices are desktop analytics; the web page records nothing. */
function trackDesktopPreference(
  eventName: string,
  props: Record<string, unknown>,
) {
  if (isTauri()) track(eventName, { source: "settings", ...props });
}

function errorProperty(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.slice(0, 200);
}

export function VoicePage() {
  const t = useT(settingsVoiceMessages);
  const desktop = isTauri();
  const enabled = useTtsSettingsStore((s) => s.enabled);
  const toggleEnabled = useTtsSettingsStore((s) => s.toggleEnabled);
  const denkerVoiceId = useTtsSettingsStore((s) => s.denkerVoiceId);
  const setDenkerVoice = useTtsSettingsStore((s) => s.setDenkerVoice);
  const windowsVoiceId = useTtsSettingsStore((s) => s.windowsVoiceId);
  const setWindowsVoice = useTtsSettingsStore((s) => s.setWindowsVoice);
  const quietWhenHeard = useTtsSettingsStore((s) => s.quietWhenHeard);
  const setQuietWhenHeard = useTtsSettingsStore((s) => s.setQuietWhenHeard);
  const {
    engine,
    isSelectable: engineIsSelectable,
    chosen: voiceEngineSetting,
    choose: chooseEngine,
  } = useVoiceEngineChoice({
    onChoose: (next) =>
      trackDesktopPreference("desktop_voice_engine_selected", { engine: next }),
  });
  const [voiceReady, setVoiceReady] = useState(isDenkerAgentVoiceAudioReady);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Switching back to the Denker voices has to load the voice worker the
  // Windows engine never needed, so this re-runs on an engine change.
  useEffect(() => {
    let cancelled = false;
    setVoiceReady(isDenkerAgentVoiceAudioReady());
    void warmDenkerAgentVoiceAudio()
      .then(() => {
        if (!cancelled) setVoiceReady(true);
      })
      .catch((error) => {
        if (!cancelled) setPreviewError(errorProperty(error));
      });
    return () => {
      cancelled = true;
    };
  }, [voiceEngineSetting]);

  // Re-render when the installed Windows voices change, including after the
  // user adds one in Windows Settings and comes back.
  useSyncExternalStore(subscribeSystemVoices, systemVoiceMappingRevision);
  useEffect(() => {
    if (!isVoiceEngineSelectable()) return;
    void ensureSystemVoicesResolved();
    const onFocus = () => refreshWindowsSystemVoices();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const windowsEngine = engine === "system" && engineIsSelectable;
  const voiceOptions = windowsEngine
    ? windowsSystemVoices().map((voice) => ({
        value: voice.id,
        label: voice.name,
      }))
    : DENKER_AGENT_VOICES.map((voice) => ({
        value: voice.id,
        label: voice.name,
      }));
  const voiceValue = (windowsEngine ? windowsVoiceId : denkerVoiceId) ?? "";
  const pocket = usePocketVoiceDownload(desktop && engine === "kokoro");
  const engineHint = VOICE_ENGINE_OPTIONS.find(
    (option) => option.id === engine,
  )?.hint;

  const chooseDictation = useCallback((next: DictationEngineChoice) => {
    if (next === "whisper") requestWhisperMicrophone();
    trackDesktopPreference("desktop_dictation_engine_selected", {
      engine: next,
    });
  }, []);

  const toggleReadAloud = useCallback(() => {
    trackDesktopPreference("desktop_voice_read_aloud_toggled", {
      enabled: !enabled,
    });
    toggleEnabled();
  }, [enabled, toggleEnabled]);

  const pickVoice = useCallback(
    (voiceId: string) => {
      if (windowsEngine) {
        setWindowsVoice(voiceId);
        trackDesktopPreference("desktop_windows_voice_selected", {
          voice_id: voiceId,
        });
        return;
      }
      setDenkerVoice(voiceId as DenkerAgentVoiceId);
      primeDenkerAgentVoiceAudio();
      trackDesktopPreference("desktop_voice_selected", {
        voice_id: voiceId,
        previous_voice_id: denkerVoiceId,
      });
    },
    [denkerVoiceId, setDenkerVoice, setWindowsVoice, windowsEngine],
  );

  const preview = useCallback(async () => {
    if (!voiceValue) return;
    setPreviewing(true);
    setPreviewError(null);
    try {
      if (windowsEngine) {
        await previewWindowsSystemVoice(voiceValue);
      } else {
        trackDesktopPreference("desktop_voice_preview_started", {
          voice_id: voiceValue,
        });
        if (await previewDenkerAgentVoice(voiceValue as DenkerAgentVoiceId)) {
          trackDesktopPreference("desktop_voice_preview_completed", {
            voice_id: voiceValue,
          });
        }
      }
    } catch (error) {
      const message = errorProperty(error);
      setPreviewError(message);
      trackDesktopPreference("desktop_voice_preview_failed", {
        voice_id: voiceValue,
        error: message,
      });
    } finally {
      setPreviewing(false);
    }
  }, [voiceValue, windowsEngine]);

  const openSpeechSettings = useCallback(async () => {
    const error = await openWindowsSpeechSettingsSafely();
    if (error) setPreviewError(t("windowsSettingsFailed", { error }));
  }, [t]);

  return (
    <div data-testid="settings-page-preferences-voice">
      <PocketSection title={t("speaking")} testId="voice-speaking">
        <PocketRow
          testId="settings-row-voice-read-aloud"
          title={t("readRepliesAloud")}
          trailing={
            <ToggleSwitch
              checked={enabled}
              onCheckedChange={toggleReadAloud}
              aria-label={t("readRepliesAloud")}
              data-testid="voice-read-aloud-toggle"
            />
          }
        />
        {enabled && voiceOptions.length > 0 ? (
          <PocketRow
            testId="settings-row-voice-voice"
            title={t("voice")}
            secondLine={pocketVoiceLine(pocket.download, pocket.retry)}
            trailing={
              <>
                {desktop && voiceValue ? (
                  <Button
                    variant="ghost"
                    size="iconSm"
                    aria-label={t("playSample")}
                    disabled={previewing || (!windowsEngine && !voiceReady)}
                    onClick={() => void preview()}
                    className="hover:bg-[var(--color-interactive-hover)]"
                    data-testid="voice-preview"
                  >
                    <Icons.Play className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
                <SelectField
                  variant="value"
                  value={voiceValue}
                  valueLabel={voiceValue ? undefined : t("optionDefault")}
                  options={voiceOptions}
                  onChange={pickVoice}
                  testId="voice-voice"
                  ariaLabel={t("voice")}
                />
              </>
            }
            below={
              <SettingsFieldError
                error={previewError}
                testId="voice-preview-error"
              />
            }
          />
        ) : null}
        {enabled && windowsEngine ? (
          <PocketRow
            testId="settings-row-voice-add-voices"
            title={t("addMoreVoices")}
            trailing={
              <Icons.ExternalLink
                className="h-3 w-3 text-subtle"
                aria-hidden="true"
              />
            }
            onSelect={() => void openSpeechSettings()}
          />
        ) : null}
        {enabled && desktop ? (
          <PocketRow
            testId="settings-row-voice-quiet-when-heard"
            title={t("quietWhenHeard")}
            secondLine={t("quietWhenHeardLine")}
            trailing={
              <ToggleSwitch
                checked={quietWhenHeard}
                onCheckedChange={(quiet) => {
                  setQuietWhenHeard(quiet);
                  trackDesktopPreference(
                    "desktop_voice_quiet_when_heard_toggled",
                    {
                      enabled: quiet,
                    },
                  );
                }}
                aria-label={t("quietWhenHeard")}
                data-testid="voice-quiet-when-heard-toggle"
              />
            }
          />
        ) : null}
        {engineIsSelectable ? (
          <PocketRow
            testId="settings-row-voice-engine"
            title={t("engine")}
            secondLine={engineHint}
            trailing={
              <SelectField
                variant="value"
                value={engine}
                options={VOICE_ENGINE_OPTIONS.map((option) => ({
                  value: option.id,
                  label: option.label,
                }))}
                onChange={(next) => chooseEngine(next as VoiceEngineChoice)}
                testId="voice-engine"
                ariaLabel={t("voiceEngine")}
              />
            }
          />
        ) : null}
      </PocketSection>
      {desktop ? (
        <PocketSection title={t("listening")} testId="voice-listening">
          <VoiceMicrophoneRow
            onChoose={(choice) =>
              trackDesktopPreference(
                "desktop_voice_input_device_selected",
                choice,
              )
            }
          />
          <VoiceLanguageRow />
          <VoiceDictationEngineRow onChoose={chooseDictation} />
          <VoiceWakeRow />
        </PocketSection>
      ) : null}
      <VoiceDictationSection />
      <VoiceRecentDictations />
    </div>
  );
}
